import type {LicenseInfo} from '@github-ui/code-view-sidebar/types'
import {useMemo} from 'react'
import {RepoSubtitle, RepoTitle} from './components/RepoTitle'
import {CodeViewHeaderActions} from './components/RepoHeaderActions'
import {RepoMobileDetails} from './components/RepoMobileDetails'
import type {HeaderResponse} from './types'
import {deriveRepoPaths} from './utils/repo-paths'
import {AdvisoryWorkspaceHeader} from './components/AdvisoryWorkspaceHeader'
import styles from './CodeViewHeader.module.css'

/** Data extracted from overviewFiles for the mobile about section. */
export interface OverviewFilesData {
  license?: LicenseInfo
  hasReadme?: boolean
  hasCodeOfConduct?: boolean
  hasContributing?: boolean
  hasSecurityPolicy?: boolean
  hasAccessibility?: boolean
}

/** Repository overview page header component for the code view. */
export function CodeViewHeader({
  ownerLogin,
  repoName,
  viewer,
  repo,
  advisoryInfo,
  showDemoNotification,
  hasAccessToCodespaces,
  hasCodeOfConduct,
  hasContributing,
  hasSecurityPolicy,
  hasAccessibility,
  ...rest
}: HeaderResponse & OverviewFilesData & {hasAccessToCodespaces?: boolean}) {
  const paths = useMemo(() => deriveRepoPaths(ownerLogin, repoName), [ownerLogin, repoName])

  if (!viewer?.isLoggedIn) {
    // The logged-out experience renders the header as part of the Rails app.
    // `viewer` may be absent when the header payload is incomplete, in which
    // case we treat it as logged-out and defer to the Rails-rendered header.
    return null
  }

  return (
    <>
      {repo.isAdvisoryWorkspace && <AdvisoryWorkspaceHeader repo={repo} advisoryInfo={advisoryInfo} />}
      <div className="tmp-pt-3 tmp-pb-3 border-bottom color-border-muted">
        <div className="hide-whenNarrow">
          <div className="d-none d-md-flex flex-wrap flex-items-center gapx-3 gapy-1">
            <div className={styles.TitleWrapper}>
              <RepoTitle
                repo={repo}
                ownerLogin={ownerLogin}
                repoName={repoName}
                paths={paths}
                isOrgOwned={rest.pin.isOrgOwned}
                showDemoNotification={showDemoNotification}
              />
            </div>
            {!repo.isAdvisoryWorkspace && (
              <div>
                <CodeViewHeaderActions
                  {...rest}
                  ownerLogin={ownerLogin}
                  repoName={repoName}
                  viewer={viewer}
                  repo={repo}
                  paths={paths}
                  hasAccessToCodespaces={hasAccessToCodespaces}
                />
              </div>
            )}
          </div>
          <RepoSubtitle repo={repo} advisoryInfo={advisoryInfo} />
        </div>
        {!repo.isAdvisoryWorkspace && (
          <div className="show-whenNarrow">
            <RepoMobileDetails
              ownerLogin={ownerLogin}
              repoName={repoName}
              {...rest}
              repo={repo}
              viewer={viewer}
              paths={paths}
              hasAccessToCodespaces={hasAccessToCodespaces}
              hasCodeOfConduct={hasCodeOfConduct}
              hasContributing={hasContributing}
              hasSecurityPolicy={hasSecurityPolicy}
              hasAccessibility={hasAccessibility}
            />
          </div>
        )}
      </div>
    </>
  )
}
